import { useState, useMemo, forwardRef, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router';
import { Link } from 'react-router-dom';
import { MenuItem, DropdownMenu, IconButton } from 'components';
import {
  PauseAction,
  Duplicate,
  Pen,
  Archive,
  DotMenuIcon,
  StartIcon,
  PictureIcon,
} from 'assets/icons';
import { getDynamicUrl, showPolipop, isDateWasReached } from 'utils';
import { routes } from 'config';
import { saveCampaignServiceAPI } from 'services/saveCampaign.service';
import { ManageCampaignsContext } from 'contexts';

const getListOfMenuItems = ({
  campaignStatus,
  _hasBanners,
  // _hasTracking,
  banner_list_no_tracking,
  // payment_status,
  bid_type,
  schedulerEndDate,
}) => {
  if (campaignStatus === 9) return ['duplicate'];
  if (campaignStatus === 0) return ['pause_now', 'edit_campaign', 'duplicate', 'archive'];
  if (campaignStatus == 3) {
    if (_hasBanners === 0) return ['edit_campaign', 'add_creative', 'archive'];
    if (banner_list_no_tracking !== '') return ['edit_campaign', 'add_tracking', 'archive'];

    if (bid_type === 2 && _hasBanners === 0) return ['edit_campaign', 'add_creative', 'archive'];
    return isDateWasReached(schedulerEndDate)
      ? ['edit_campaign', 'duplicate', 'archive']
      : ['play_now', 'edit_campaign', 'duplicate', 'archive'];
  }

  if (campaignStatus === 8 || campaignStatus === 4)
    return ['edit_campaign', 'duplicate', 'archive'];
};

export const DotsMenu = (props) => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const { openTrackingModal, setIsStatusLoading } = useContext(ManageCampaignsContext);

  const [updateCampaignStatus, resultApp] =
    saveCampaignServiceAPI.useUpdateCampaignStatusMutation();
  const [updateWebCampaignStatus, resultWeb] =
    saveCampaignServiceAPI.useUpdateWebCampaignStatusMutation();

  const {
    campaignStatus,
    // campaign,
    campaignId: cmp_id,
    _hasBanners,
    banner_list_no_tracking,
    _hasTracking,
    payment_status,
    bid_type,
    schedulerEndDate,
    isWebsite,
  } = props.data;

  // const rowIndex = props.rowIndex;

  // const [action, result] = manageCampaignsAPI.useLazyGetCampaignsByDateQuery();
  // const dateForRequest = useSelector((state) => state.manageCampaigns.selectedDateRange);
  // const isDeletedCampaign = useMemo(() => {
  //   return campaignStatus === 9;
  // }, [campaignStatus]);

  const editAction = (env) => {
    setIsOpen(false);
    env === 'open_tracking' &&
      localStorage.setItem('sectionToOpen', JSON.stringify('conversionTracking'));
    env === 'open_creative' && localStorage.setItem('sectionToOpen', JSON.stringify('creatives'));
    let url = getDynamicUrl(routes.list.find((route) => route.id === 'campaign/editCampaign').url, {
      cmp_id,
    });
    navigate(url);
  };

  const duplicateAction = () => {
    setIsOpen(false);
    let url = getDynamicUrl(
      routes.list.find((route) => route.id === 'campaign/duplicateCampaign').url,
      {
        cmp_id,
      },
    );
    navigate(url);
  };

  const statusAction = (status) => {
    setIsOpen(false);
    const statusReq = {
      campaignId: props.data.campaignId,
      status: status,
    };
    if (isWebsite) {
      updateWebCampaignStatus(statusReq);
    } else {
      updateCampaignStatus(statusReq);
    }
  };

  const result = isWebsite ? resultWeb : resultApp;

  const menuItemsList = useMemo(
    () =>
      getListOfMenuItems({
        campaignStatus,
        _hasBanners,
        _hasTracking,
        payment_status,
        bid_type,
        banner_list_no_tracking,
        schedulerEndDate,
      }),
    [campaignStatus, _hasBanners, _hasTracking, payment_status, bid_type, banner_list_no_tracking],
  );

  useEffect(() => {
    if (
      !isWebsite &&
      result.data &&
      result.data.action_cancelled &&
      (!result.data.s2s_tracking_enabled || !result.data.s2s_tracking_cpc_enabled)
    ) {
      const url = getDynamicUrl(
        routes.list.find((route) => route.id === 'campaign/editCampaign').url,
        {
          cmp_id,
        },
      );
      openTrackingModal(url);
      return;
    }
    if (result.isSuccess || result.isError) {
      showPolipop(result);
    }
  }, [result.status]);

  useEffect(() => {
    setIsStatusLoading(result.isLoading);
  }, [result.isLoading]);

  // const menuItems = useMemo(
  //   () => [
  //     { menu_name: 'Edit Campaign', id: 'edit_campaign', icon: Pen, action: editAction },
  //     { menu_name: 'Duplicate', id: 'duplicate', icon: Duplicate, action: duplicateAction },
  //     {
  //       menu_name: 'Archive',
  //       id: 'archive',
  //       icon: Archive,
  //       action: () => statusAction(9),
  //     },
  //   ],
  //   [],
  // );

  const allMenuItems = useMemo(
    () => [
      {
        menu_name: 'Play Now',
        id: 'play_now',
        icon: StartIcon,
        action: () => statusAction(0),
      },
      {
        menu_name: 'Pause Now',
        id: 'pause_now',
        icon: PauseAction,
        action: () => statusAction(3),
      },
      {
        menu_name: 'Edit Campaign',
        id: 'edit_campaign',
        icon: Pen,
        component: Link,
        action: () => editAction('edit_campaign'),
        to: `${getDynamicUrl(
          routes.list.find((route) => route.id === 'campaign/editCampaign').url,
          {
            cmp_id,
          },
        )}`,
      },
      {
        menu_name: 'Add Creative',
        id: 'add_creative',
        icon: PictureIcon,
        action: () => editAction('open_creative'),
      },
      {
        menu_name: 'Add Tracking',
        id: 'add_tracking',
        icon: PictureIcon,
        action: () => editAction('open_tracking'),
      },
      {
        menu_name: 'Duplicate',
        id: 'duplicate',
        icon: Duplicate,
        action: duplicateAction,
        component: Link,
        to: `${getDynamicUrl(
          routes.list.find((route) => route.id === 'campaign/duplicateCampaign').url,
          {
            cmp_id,
          },
        )}`,
      },
      {
        menu_name: 'Archive',
        id: 'archive',
        icon: Archive,
        action: () => statusAction(9),
      },
    ],
    [],
  );

  return (
    <DropdownMenu isOpen={isOpen} setIsOpen={setIsOpen} BtnElement={DropdownBtn}>
      {/* {campaignStatus === 3 && (
        <MenuItem
          height={40}
          icon={StartIcon}
          value='Play Now'
          id='play_now'
          onClick={() => statusAction(0)}
        >
          Play Now
        </MenuItem>
      )}
      {campaignStatus === 0 && (
        <MenuItem
          height={40}
          icon={PauseAction}
          value='Pause Now'
          id='pause_now'
          onClick={() => statusAction(3)}
        >
          Pause Now
        </MenuItem>
      )} */}
      <ul style={{ all: 'unset' }}>
        {allMenuItems.map((item) => {
          const isShow = menuItemsList?.includes(item.id);
          if (!isShow) return null;
          return (
            <MenuItem
              as={item.component}
              to={item.to}
              height={40}
              icon={item.icon}
              value={item.menu_name}
              id={item.id}
              onClick={item.action}
              key={item.id}
            >
              {item.menu_name}
            </MenuItem>
          );
        })}
      </ul>
    </DropdownMenu>
  );
};

const DropdownBtn = forwardRef((props, ref) => {
  return (
    <IconButton.SmallSize
      onClick={props.onClick}
      ref={ref}
      icon={DotMenuIcon}
      type={IconButton.btnTypes.tertiary}
      isPressed={props.isPressed}
    />
  );
});
DropdownBtn.displayName = 'DropdownBtn';
